import { cn } from "@/lib/utils"

interface PlaybackControlsProps {
  message: string
  isPlaying: boolean
  atStart: boolean
  atEnd: boolean
  stepIndex: number
  totalSteps: number
  onStepBack: () => void
  onStepForward: () => void
  onPlay: () => void
  onPause: () => void
  onReset: () => void
}

function IconButton({
  onClick,
  disabled,
  label,
  children,
}: {
  onClick: () => void
  disabled?: boolean
  label: string
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      className={cn(
        "w-9 h-9 flex items-center justify-center rounded-md transition",
        disabled
          ? "opacity-30 cursor-default"
          : "hover:bg-accent cursor-pointer text-foreground"
      )}
    >
      {children}
    </button>
  )
}

export function PlaybackControls({
  message,
  isPlaying,
  atStart,
  atEnd,
  stepIndex,
  totalSteps,
  onStepBack,
  onStepForward,
  onPlay,
  onPause,
  onReset,
}: PlaybackControlsProps) {
  return (
    <div className="flex flex-col gap-2">
      {/* Step message */}
      <div className="rounded-md bg-muted px-3 py-2 text-sm min-h-[2.25rem] flex items-center">
        <span className="text-muted-foreground">
          {stepIndex < 0 ? "Press play or step forward to begin" : message}
        </span>
      </div>

      {/* Controls row */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1">
          {/* Reset */}
          <IconButton onClick={onReset} disabled={atStart} label="Reset">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M2 2v5h5" />
              <path d="M2.5 7A6 6 0 1 1 3 10" />
            </svg>
          </IconButton>

          {/* Step back */}
          <IconButton onClick={onStepBack} disabled={atStart} label="Step back">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
              <rect x="2" y="3" width="2" height="10" rx="0.5" />
              <polygon points="13,3 6,8 13,13" />
            </svg>
          </IconButton>

          {/* Play / Pause */}
          {isPlaying ? (
            <IconButton onClick={onPause} label="Pause">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                <rect x="3" y="2" width="3.5" height="12" rx="0.5" />
                <rect x="9.5" y="2" width="3.5" height="12" rx="0.5" />
              </svg>
            </IconButton>
          ) : (
            <IconButton onClick={onPlay} disabled={atEnd} label="Play">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                <polygon points="3,2 14,8 3,14" />
              </svg>
            </IconButton>
          )}

          {/* Step forward */}
          <IconButton onClick={onStepForward} disabled={atEnd} label="Step forward">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
              <polygon points="3,3 10,8 3,13" />
              <rect x="12" y="3" width="2" height="10" rx="0.5" />
            </svg>
          </IconButton>
        </div>

        {/* Step counter */}
        <span className="text-xs text-muted-foreground tabular-nums">
          {Math.max(0, stepIndex + 1)} / {totalSteps}
        </span>
      </div>
    </div>
  )
}
